import React, { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isEmpty, get } from 'lodash';
import update from 'immutability-helper';
import { getFirstAppliedFilter, currentMetricsMetaSelector } from '@store/common/selectors';
import { findDimensionDetailsInCompareFilters } from '@src/utils/filter/getDimensionTypesFromFilter';
import { dimensionByTypesSelector } from '@store/common/dimensionList/selectors';
import { isAppModule } from '@utils/constants';
import { useCompareData } from '@components/metrics-panel-hz/hooks/useCompareSeries';
import { DATA_TAGS } from '@components/metrics-panel-hz/hooks/useData';
import { metricSelector } from '@store/metricsChartContainer/selectors';
import Table from '@components/common/Table';
import { getRulesFromFilter } from '@utils';
import { calculatePctShareMax } from '@utils/table/pctShare';
import { METRICS_TYPES_EXCLUDE_BY_FILTER } from '@utils/metrics';
import { buildColumns, calcHeight, ROW_HEIGHT, HEADER_HEIGHT } from './utils';
import { TableWrapper } from './styled';
import Row from './Row';
import TableHeaderCell from './TableHeaderCell';
import { highlightCompareFilter } from '@src/store/trends/base/actions';
import { compareOnTrendsFiltersMetaSelector } from '@src/store/trends/base/selectors';

function SummaryTable() {
  const { totals, compareFilters } = useCompareData({ dataTag: DATA_TAGS.compare });
  const currentMetricsMeta = useSelector(currentMetricsMetaSelector);
  const metrics = useSelector(metricSelector);
  const [unit, setUnit] = useState('#');
  const parentFilter = useSelector(getFirstAppliedFilter);
  const [expand, setExpand] = useState(false);
  const dimensionByTypes = useSelector(dimensionByTypesSelector);
  const dispatch = useDispatch();

  const compareFiltersMetaMap = useSelector(compareOnTrendsFiltersMetaSelector);

  // app NA exclusions
  // copied over from compare dashboard
  const excludedMetricTypes = useMemo(() => {
    const excludeList: any = [];
    try {
      if (isAppModule && !isEmpty(dimensionByTypes)) {
        // compare filters merged with parent filter
        const allFilterTags = compareFilters.map((cf) => {
          const subFilterRules = update(parentFilter.rule, { $push: [...cf.rule] });
          return { filters: getRulesFromFilter(subFilterRules, true) };
        });

        const { filterDimensionTypes } = findDimensionDetailsInCompareFilters(
          allFilterTags,
          dimensionByTypes,
        );
        if (filterDimensionTypes && Array.isArray(filterDimensionTypes)) {
          filterDimensionTypes.forEach((eachFilterTags: any, index) => {
            excludeList[index] = [];
            eachFilterTags.forEach((tag: any) => {
              excludeList[index] = [...excludeList[index], ...METRICS_TYPES_EXCLUDE_BY_FILTER[tag]];
            });
          });
        }
      }
    } catch (err) {
      // pass
    }
    return excludeList;
  }, [compareFilters, dimensionByTypes]);

  const columns: any = useMemo(() => {
    const handleUnitChange = (value) => {
      setUnit(value);
    };
    const handleExpandChange = () => {
      setExpand((expand) => !expand);
    };
    return buildColumns({
      metrics,
      unit,
      productMetrics: currentMetricsMeta,
      excludedMetricTypes,
      handleUnitChange,
      handleExpandChange,
    });
  }, [metrics, unit, currentMetricsMeta, excludedMetricTypes, setUnit, setExpand]);

  const { data, pctShareMaxValues } = useMemo(() => {
    const pctShareMaxValues = calculatePctShareMax({ data: totals });
    const parsedTotals = totals.map((row, index) => {
      return {
        ...row,
        filter: compareFilters[index],
        id: `${JSON.stringify(compareFilters[index])}-${index}`,
      };
    });
    return { data: parsedTotals, pctShareMaxValues };
  }, [totals, compareFilters]);

  if (!totals || totals.length === 0) {
    return null;
  }

  const onRowClick = ({ rowIndex }) => {
    const filter = compareFilters[rowIndex];
    const meta = compareFiltersMetaMap.get(filter.rule);
    const highlight = get(meta, ['highlight'], false);
    dispatch(highlightCompareFilter(rowIndex, !highlight));
  };

  const getRowClass = ({ rowIndex }) => {
    const filter = compareFilters[rowIndex];
    const meta = compareFiltersMetaMap.get(filter.rule);
    const highlight = get(meta, ['highlight'], false);

    return highlight ? 'selected' : '';
  };

  const wrapperHeight = calcHeight(data.length, expand);

  return (
    <div>
      <TableWrapper style={{ height: wrapperHeight }}>
        <Table
          data={data}
          columns={columns}
          headerHeight={HEADER_HEIGHT}
          rowHeight={ROW_HEIGHT}
          fixed
          // maxHeight={280}
          rowEventHandlers={{ onClick: onRowClick }}
          rowClassName={getRowClass}
          rowRenderer={Row}
          components={{ TableHeaderCell }}
          headerProps={{ unit, pctShareMaxValues, expand }}
          //rowKey="filter"
        />
      </TableWrapper>
    </div>
  );
}

export default SummaryTable;
